import { ChangeEvent, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton } from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { Box, Divider, Typography } from "@mui/material";
import { FilterSidebarDynamicDataFilter } from "./components/FilterSidebarDynamicDataFilter";
import { FilterSidebarSelectBox } from "./components/FilterSidebarSelectBox";
import {
  FilterSidebarInput,
  FilterSidebarItem,
  FilterSidebarProps,
} from "./FilterSidebar.types";
import { useFilterSidebar } from "./useFilterSidebar";

export function FilterSidebar<FilterType extends FilterSidebarInput>(
  props: FilterSidebarProps<FilterType>
) {
  const {
    groupByOptions,
    sortByOptions,
    onResetFilters,
    onApplyFilters,
    filters,
    initialFilters,
    resetButtonTitle,
    searchButtonTitle,
    sidebarTitle,
    enableSearchParamsSync = false,
    dataFilters = {},
    SidebarHeaderComponent,
    loading,
    baseUrlForFilteredSearch,
  } = props;
  const { classes } = useStyles();

  const { localFilters, initialize, setDataFilter, reset, applyFilters } =
    useFilterSidebar<FilterType>({
      filters,
      initialFilters,
      enableSearchParamsSync,
      onApplyFilters,
      baseUrlForFilteredSearch,
    });

  const handleResetFilters = () => {
    reset();
    if (onResetFilters) {
      onResetFilters();
    }
  };

  const renderDataFilterInput = (dataFilter: FilterSidebarItem<FilterType>) => {
    const { field, label, renderOption, data, mapDataToOption } = dataFilter;

    const options = mapDataToOption ? data.map(mapDataToOption) : data;

    return (
      <FilterSidebarDynamicDataFilter
        label={label}
        options={options}
        value={localFilters[field]}
        fieldKey={String(field)}
        onChange={(fieldKey, value) => {
          setDataFilter(fieldKey as keyof FilterType, value);
        }}
        renderOption={renderOption}
        loading={loading}
      />
    );
  };

  useEffect(() => {
    initialize();
  }, []);

  return (
    <Box className={classes.root}>
      {SidebarHeaderComponent ? (
        <SidebarHeaderComponent />
      ) : (
        <Typography variant="body2" fontWeight={600}>
          {sidebarTitle || "Filter & Sort"}
        </Typography>
      )}
      <Divider className={classes.divider} />
      <Box className={classes.contentWrapper}>
        <Box className={classes.filtersContainer}>
          <FilterSidebarSelectBox
            label="Group By"
            options={groupByOptions}
            value={localFilters.groupBy}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
              setDataFilter("groupBy", event.target.value);
            }}
            loading={loading}
          />
          <FilterSidebarSelectBox
            label="Sort By"
            options={sortByOptions}
            value={localFilters.sortBy}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
              setDataFilter("sortBy", event.target.value);
            }}
            loading={loading}
          />
          {Object.values(dataFilters).map(renderDataFilterInput)}
        </Box>
        <Box className={classes.buttonsContainer}>
          <MiButton
            title={resetButtonTitle || "Reset Filters"}
            onClick={handleResetFilters}
            variant={BUTTON_VARIANTS.OUTLINED}
            className={classes.button}
          />
          <MiButton
            title={searchButtonTitle || "Filtered Search"}
            onClick={applyFilters}
            variant={BUTTON_VARIANTS.CONTAINED}
            className={classes.button}
          />
        </Box>
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
  },
  divider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "calc(100% - 32px)",
  },
  filtersContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 11,
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    flex: 1,
    gap: theme.spacing(2),
    alignItems: "center",
    width: "100%",
  },
  button: {
    width: "100%",
  },
}));
